import type { AxiosError } from 'axios';
import axios from 'axios';
import type { RaygunV2UserDetails } from 'raygun4js';
import rg4js from 'raygun4js';

import type { UserRole } from '../enum';
import { environment } from '../environment/environment';

export type RaygunUser = {
  user_id: string | null;
  organization_id: string | null;
  role: UserRole | null;
  region: string | null;
};

const isLocal = environment.environment_deploy === 'local';

rg4js('options', {
  disableErrorTracking: isLocal,
});
rg4js('apiKey', environment.raygunKey);
rg4js('setVersion', `${environment.versionNumber}.${environment.buildNumber}`);
rg4js('enableCrashReporting', !isLocal);
rg4js('withTags', [environment.environment_deploy, `v${environment.versionNumber}`]);
rg4js('filterSensitiveData', ['password', 'pass', 'new_password', 'old_password']);

export const RaygunErrorHandlerService = () => {
  const logError = (err: unknown, tags: string[]) => {
    if (axios.isCancel(err)) {
      return;
    }

    const error = err as AxiosError;
    if (error.response?.status) {
      tags = [error.response.status.toString(), ...tags];
    }

    const data = { error, tags };

    if (!isLocal && navigator.onLine) {
      rg4js('send', data);
    }

    if (isLocal && !(error.response && error.response.status === 401)) {
      // oxlint-disable-next-line no-console
      console.error(data);
    }
  };

  /**
   * example:
   *
   * rg4js('setUser', {
   *  identifier: 'user_email_address@localhost.local',
   *  isAnonymous: false,
   *  email: 'emailaddress@localhost.local',
   *  firstName: 'Foo',
   *  fullName: 'Foo Bar',
   *  uuid: 'BAE62917-ACE8-ab3D-9287-B6A33B8E8C55'
   * });
   */

  const setRaygunUser = async (user: RaygunUser) => {
    const details: RaygunV2UserDetails = {
      identifier: `Role: ${user.role}, User id: ${user.user_id}, Org id: ${user.organization_id}, Region: ${user.region}`,
    };
    rg4js('setUser', details);
  };

  const resetRaygunUser = () => {
    const details: RaygunV2UserDetails = { identifier: '' };
    rg4js('setUser', details);
  };

  return { logError, setRaygunUser, resetRaygunUser };
};
